import { Form, Formik, FormikHelpers } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { DetailField } from "@volca/ui/components/ui/detail-field";
import { Label } from "@volca/ui/components/ui/label";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { toast } from "@volca/ui/lib/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { formatDate } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { formatPhoneNumber } from "@/core/utils/phone";
import { useGetPayout, useUpdatePayout } from "@/payouts/query";
import { ReferralPaymentsSidebarSkeleton } from "./referral-payments-sidebar-skeleton";

interface ReferralPaymentFormValues {
  notes: string;
}

interface ReferralPaymentsSidebarProps {
  referralPaymentsId: string;
  businessId?: string;
}

const validationSchema = Yup.object().shape({
  notes: Yup.string().optional(),
});

const ReferralPaymentsSidebar = ({
  referralPaymentsId,
  businessId,
}: ReferralPaymentsSidebarProps) => {
  const { data: payout, isLoading } = useGetPayout(
    businessId,
    referralPaymentsId,
  );

  const { mutate: updatePayout } = useUpdatePayout(businessId);
  const { resetSidebar } = useBusinessSettingsSidebar();
  const [isEditingNote, setIsEditingNote] = useState(false);

  if (isLoading) {
    return (
      <BusinessSettingsSidebarWrapper>
        <Spinner layout="centered" />
      </BusinessSettingsSidebarWrapper>
    );
  }

  if (!payout) {
    return (
      <BusinessSettingsSidebarWrapper>
        <div className="p-5">Payment not found.</div>
      </BusinessSettingsSidebarWrapper>
    );
  }

  const potentialCustomerNameOrPhone = useMemo(
    () =>
      payout.referral?.potential_customer_name ||
      (payout.referral?.potential_customer_phone_number
        ? formatPhoneNumber(payout.referral.potential_customer_phone_number)
        : "N/A"),
    [payout.referral],
  );

  const advocateNameOrPhone = useMemo(
    () =>
      payout.referral?.advocate_name ||
      (payout.referral?.advocate_phone_number
        ? formatPhoneNumber(payout.referral.advocate_phone_number)
        : "N/A"),
    [payout.referral],
  );

  const initialValues = useMemo<ReferralPaymentFormValues>(
    () => ({
      notes: payout.notes || "",
    }),
    [payout],
  );

  const handleSubmit = async (
    values: ReferralPaymentFormValues,
    { resetForm }: FormikHelpers<ReferralPaymentFormValues>,
  ) =>
    updatePayout(
      {
        ...values,
        id: payout.id,
      },
      {
        onSuccess: () => {
          resetForm({ values });
          toast.success("Referral payment updated successfully");
          setIsEditingNote(false);
        },
        onError: (error) => {
          toast.error("Failed to update referral payment", {
            description:
              error instanceof Error ? error.message : "Please try again",
          });
          setIsEditingNote(false);
        },
      },
    );

  const headerText = "Payments";
  const name = "Payment made";

  if (isLoading) {
    return <ReferralPaymentsSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
      />
      <div className="flex-1 overflow-hidden">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ getFieldProps, dirty, resetForm }) => (
            <Stack
              align="start"
              gap={6}
              className="flex h-full w-full flex-col overflow-hidden"
            >
              <Form className="w-full flex-none px-6 pt-6">
                <Stack className="w-full" align="start" gap={6}>
                  <Stack
                    className="w-full"
                    align="start"
                    gap={4}
                    direction="row"
                  >
                    <DetailField
                      label="Customer"
                      message={potentialCustomerNameOrPhone}
                      extraInfo={
                        <>
                          This customer booked a job with you through{" "}
                          <b>{advocateNameOrPhone}</b>
                          <br />
                          We use their name or phone number to help you identify
                          them and all of the things that happen related to the
                          job you did for them.
                        </>
                      }
                    />
                    <DetailField
                      label="Advocate"
                      message={advocateNameOrPhone}
                      extraInfo={
                        <>
                          This is the customer that referred{" "}
                          <b>{potentialCustomerNameOrPhone}</b> to you
                        </>
                      }
                    />
                  </Stack>

                  <Stack
                    className="w-full"
                    align="start"
                    gap={4}
                    direction="row"
                  >
                    <DetailField
                      label="Date paid"
                      message={formatDate(payout.created_at)}
                    />
                    <DetailField
                      label="Initiated by"
                      message={`${payout.approver.first_name ?? ""} ${payout.approver.last_name ?? ""}`}
                      extraInfo={
                        <>
                          This is the user who initiated the payment to{" "}
                          <b>{advocateNameOrPhone}</b>
                        </>
                      }
                    />
                  </Stack>

                  <DetailField
                    label="Payment amount"
                    message={formatMoneyCents(payout.amount)}
                  />

                  <Stack align="start" gap={4} className="relative w-full">
                    {payout.notes && !isEditingNote ? (
                      <DetailField label="Note" message={payout.notes} />
                    ) : (
                      <Stack className="w-full" gap={2}>
                        <Label htmlFor="referral-payment-note">Note</Label>
                        <Textarea
                          id="referral-payment-note"
                          className="min-h-32"
                          placeholder="Enter notes"
                          {...getFieldProps("notes")}
                        />
                      </Stack>
                    )}

                    {payout.notes && !isEditingNote && (
                      <Button
                        type="button"
                        variant="link"
                        className="absolute top-0.5 right-0 h-auto p-0"
                        onClick={() => {
                          setIsEditingNote(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </Stack>

                  {(dirty || isEditingNote) && (
                    <Stack align="start" gap={4} direction="row">
                      <Button type="submit" size="sm" disabled={!dirty}>
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          setIsEditingNote(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Form>
            </Stack>
          )}
        </Formik>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default ReferralPaymentsSidebar;
